import React from 'react';
import type { CategoryOption, FileItem } from '../types';

interface Props {
  files: FileItem[];
  categories: CategoryOption[];
  selectedCategory: string;
  onSelectCategory: (cat: string) => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  fileTypeFilter: string;
  onFileTypeChange: (type: string) => void;
  statusFilter: string;
  onStatusChange: (status: string) => void;
  aiOnlyFilter: boolean;
  onAiOnlyChange: (enabled: boolean) => void;
  sortBy: string;
  onSortChange: (sort: string) => void;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onOpenPreview: (fileId: string) => void;
  onDownload: (fileId: string) => void;
  onNewVersionClick: (file: FileItem) => void;
  onManageAclClick: (file: FileItem) => void;
  onToggleAi: (fileId: string, currentEnabled: boolean) => void;
  onArchiveToggle: (fileId: string) => void;
  onCopyLink: (file: FileItem) => void;
}

export const DocumentLibraryView: React.FC<Props> = ({
  files,
  categories,
  selectedCategory,
  onSelectCategory,
  searchQuery,
  onSearchChange,
  fileTypeFilter,
  onFileTypeChange,
  statusFilter,
  onStatusChange,
  aiOnlyFilter,
  onAiOnlyChange,
  sortBy,
  onSortChange,
  page,
  pageSize,
  total,
  onPageChange,
  onOpenPreview,
  onDownload,
  onNewVersionClick,
  onManageAclClick,
  onToggleAi,
  onArchiveToggle,
  onCopyLink,
}) => {
  const getFormatBadge = (mime: string, name: string) => {
    if (mime.includes('pdf') || name.endsWith('.pdf')) return 'badge-pdf';
    if (mime.includes('spreadsheet') || mime.includes('excel') || name.endsWith('.xlsx') || name.endsWith('.csv')) {
      return 'badge-xlsx';
    }
    return 'badge-docx';
  };

  const getFormatLabel = (mime: string, name: string) => {
    if (mime.includes('pdf') || name.endsWith('.pdf')) return 'PDF';
    if (mime.includes('spreadsheet') || mime.includes('excel') || name.endsWith('.xlsx')) return 'XLSX';
    if (mime.includes('csv') || name.endsWith('.csv')) return 'CSV';
    if (mime.includes('text') || name.endsWith('.txt')) return 'TXT';
    return 'DOCX';
  };

  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const rangeFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeTo = Math.min(page * pageSize, total);
  const windowStart = Math.max(1, Math.min(page - 2, pageCount - 4));
  const windowEnd = Math.min(pageCount, windowStart + 4);
  const pageNumbers = Array.from(
    { length: Math.max(0, windowEnd - windowStart + 1) },
    (_, index) => windowStart + index,
  );

  return (
    <div className="document-library-view">
      <div className="library-controls">
        <div className="library-search-row">
          <input
            type="search"
            className="library-search-input"
            placeholder="Поиск по названию, тегу, хэшу или содержанию..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />

          <select
            className="library-select"
            value={fileTypeFilter}
            onChange={(e) => onFileTypeChange(e.target.value)}
          >
            <option value="all">Все форматы</option>
            <option value="word">Word (DOCX)</option>
            <option value="pdf">PDF документы</option>
            <option value="excel">Excel / CSV</option>
            <option value="text">Текстовые / Промпты</option>
          </select>

          <select
            className="library-select"
            value={statusFilter}
            onChange={(e) => onStatusChange(e.target.value)}
          >
            <option value="all">Все статусы</option>
            <option value="current">Только актуальные</option>
            <option value="archived">В архиве</option>
          </select>

          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', color: '#cbd5e1', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={aiOnlyFilter}
              onChange={(e) => onAiOnlyChange(e.target.checked)}
            />
            AI Source ON
          </label>

          <select
            className="library-select"
            value={sortBy}
            onChange={(e) => onSortChange(e.target.value)}
          >
            <option value="updated_at">По дате обновления</option>
            <option value="name">По названию</option>
            <option value="size">По размеру</option>
          </select>
        </div>

        <div className="library-category-pills">
          {categories.map((cat) => (
            <button
              key={cat.key}
              type="button"
              className={`category-pill-btn ${selectedCategory === cat.key ? 'active' : ''}`}
              onClick={() => onSelectCategory(cat.key)}
            >
              <span>{cat.label}</span>
              <span className="pill-count">{cat.count}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="file-table-container" style={{ marginTop: '1rem' }}>
        <table className="file-table">
          <thead>
            <tr>
              <th>ДОКУМЕНТ</th>
              <th>КАТЕГОРИЯ</th>
              <th>РАЗМЕР</th>
              <th>ВЕРСИЯ</th>
              <th>AI ИСТОЧНИК</th>
              <th>ДЕЙСТВИЯ</th>
            </tr>
          </thead>
          <tbody>
            {files.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                  Документы не найдены по указанным критериям
                </td>
              </tr>
            ) : (
              files.map((file) => {
                const badgeClass = getFormatBadge(file.mime_type, file.original_name);
                const formatLabel = getFormatLabel(file.mime_type, file.original_name);
                const isAiOn = file.ai_source?.ai_enabled ?? false;

                return (
                  <tr key={file.id} data-testid={`file-row-${file.file_id}`}>
                    <td>
                      <div className="file-row-name-col">
                        <span className={`qa-badge ${badgeClass}`}>{formatLabel}</span>
                        <div>
                          <span className="file-row-title">{file.display_name}</span>
                          <span className="file-row-folder">{file.folder} · {file.file_id}</span>
                        </div>
                      </div>
                    </td>

                    <td>
                      <span style={{ fontSize: '0.82rem', color: '#94a3b8' }}>
                        {file.category}
                      </span>
                    </td>

                    <td>
                      <span style={{ fontSize: '0.82rem', color: '#cbd5e1' }}>
                        {file.size_label}
                      </span>
                    </td>

                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                        <span className="qa-version-pill">{file.current_version_no}</span>
                        {!file.is_archived ? (
                          <span className="qa-current-pill">Актуальная</span>
                        ) : (
                          <span style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#f87171', padding: '0.1rem 0.4rem', borderRadius: '4px', fontSize: '0.72rem' }}>
                            Архив
                          </span>
                        )}
                      </div>
                    </td>

                    <td>
                      <div className="file-ai-toggle">
                        <span className={`ai-status-badge ai-status-${file.ai_source?.index_status || 'DISABLED'}`}>
                          {file.ai_source?.index_status || 'DISABLED'}
                        </span>
                        <button
                          type="button"
                          className={`ai-toggle-btn ${isAiOn ? 'enabled' : ''}`}
                          onClick={() => onToggleAi(file.file_id, isAiOn)}
                          title="Включить / отключить индексацию для AI"
                        >
                          {isAiOn ? 'AI ON' : 'AI OFF'}
                        </button>
                      </div>
                    </td>

                    <td>
                      <div className="row-actions-group">
                        <button
                          type="button"
                          className="btn-open-preview"
                          style={{ padding: '0.3rem 0.6rem', fontSize: '0.78rem' }}
                          onClick={() => onOpenPreview(file.file_id)}
                        >
                          Открыть
                        </button>
                        <button
                          type="button"
                          className="action-icon-btn"
                          onClick={() => onDownload(file.file_id)}
                          title="Скачать файл"
                        >
                          ↓ Скачать
                        </button>
                        <button
                          type="button"
                          className="action-icon-btn"
                          onClick={() => onCopyLink(file)}
                          title="Копировать внутреннюю ссылку Academia"
                        >
                          Копировать ссылку
                        </button>
                        <button
                          type="button"
                          className="action-icon-btn"
                          onClick={() => onNewVersionClick(file)}
                          title="Создать новую версию"
                        >
                          + Версия
                        </button>
                        <button
                          type="button"
                          className="action-icon-btn"
                          onClick={() => onManageAclClick(file)}
                          title="Права доступа"
                        >
                          Доступ
                        </button>
                        <button
                          type="button"
                          className="action-icon-btn"
                          style={{ color: file.is_archived ? '#34d399' : '#f87171' }}
                          onClick={() => onArchiveToggle(file.file_id)}
                          title={file.is_archived ? 'Восстановить из архива' : 'Переместить в архив'}
                        >
                          {file.is_archived ? 'Восстановить' : 'В архив'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {total > pageSize ? (
        <div className="library-pagination">
          <span>
            {rangeFrom}–{rangeTo} из {total}
          </span>
          <div className="library-pagination-pages">
            <button
              type="button"
              className="library-page-btn"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
            >
              Назад
            </button>
            {pageNumbers.map((number) => (
              <button
                key={number}
                type="button"
                className={`library-page-btn ${number === page ? 'active' : ''}`}
                onClick={() => onPageChange(number)}
              >
                {number}
              </button>
            ))}
            <button
              type="button"
              className="library-page-btn"
              disabled={page >= pageCount}
              onClick={() => onPageChange(page + 1)}
            >
              Дальше
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};
